import { reactMount } from '@/lib/reactMount'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useRef, useState, useEffect } from 'react'
import { Button } from '@/components/Button'

function makeNotificationId (n) {
  const payload = [
    n.Title.trim(),
    new Date(n.ShowFrom).toISOString(),
    new Date(n.ShowUntil).toISOString()
  ].join('|')

  // FNV-1a hash (32-bit)
  let hash = 0x811c9dc5
  for (let i = 0; i < payload.length; i++) {
    hash ^= payload.charCodeAt(i)
    hash = (hash * 0x01000193) >>> 0 // multiply & keep unsigned
  }

  // Convert to base36 string for compactness
  return 'notif_' + hash.toString(36)
}

// Custom hook for sessionStorage-backed state
function useSessionStorage (defaultValue, notificationObject) {
  // A unique key for this banner in sessionStorage
  const BANNER_SESSION_KEY = makeNotificationId(notificationObject)

  const [state, setState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = window.sessionStorage.getItem(BANNER_SESSION_KEY)
      if (stored !== null) {
        return JSON.parse(stored)
      }
    }
    return defaultValue
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(BANNER_SESSION_KEY, JSON.stringify(state))
    }
  }, [state])

  return [state, setState]
}

const bannerBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const NotificationLevel = umbraco.NotificationLevel || 'default'
  const [visible, setVisible] = useSessionStorage(true, umbraco)

  const handleDismiss = () => {
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className={`relative isolate bg-[--notification-bg] ${NotificationLevel === 'warning' ? '!bg-green-1' : ''}`} data-emergency-level={NotificationLevel}>
      <div className='mx-auto w-full justify-between items-start max-w-[1280px] flex'>
        <div className='col-span-full space-y-2 items-start justify-center px-4 py-6 sm:px-6 lg:px-8'>
          <div className='space-y-2 text-[--notification-text] pt-0.5'>
            {umbraco.Title && (
              <h3 className='text-base font-extrabold xl:pr-5 shrink-0 text-[--notification-title]'>{umbraco.Title}</h3>
            )}
            {umbraco.Description && (
              <div
                className='text-base text-[--notification-text] max-w-[860px]'
                dangerouslySetInnerHTML={{ __html: umbraco.Description }}
              />
            )}

          </div>

          {umbraco.Button?.Url && (
            <Button
              variant='link'
              href={umbraco.Button.Url}
              target={umbraco.Button?.Target || null}
              className='!pt-0 !pb-1.5'
            >
              {umbraco.Button.Name}
            </Button>
          )}
        </div>
        <button
          type='button'
          className='shrink-0 p-3 mt-4 xl:mr-5 focus-visible:outline-offset-[-4px]'
          onClick={handleDismiss}
        >
          <span className='sr-only'>Dismiss</span>
          <XMarkIcon aria-hidden='true' className='size-6 text-[--notification-title]' />
        </button>
      </div>

    </div>
  )
}

reactMount('bannerBlock', bannerBlock)
